import React, { useContext, useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import type { RouteComponentProps } from 'react-router'
import styled from 'styled-components'
import { FlightLandingPageRegionContent } from './FlightLandingPageContent'
import Pane from 'components/Common/Pane'
import GeoContext from 'contexts/geoContext'
import Image from 'components/Common/Image'
import { rem } from 'polished'
import OfferList from 'components/OfferList/OfferList'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { HOTEL_PAGE_OFFER_TYPES } from 'constants/offerList'
import LayoutContainer from 'components/Common/LayoutContainer'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import FlightsSearch from 'components/Flights/FlightsSearch/FlightsSearch'
import FlightsCreditView from 'components/Flights/CreditRebooking/FlightsCreditView'
import { isFlightsCredit } from 'selectors/flightsSelectors'
import { flightsImageId } from 'constants/flight'
import GlobalMobileSearchHeroBar from 'components/SearchV2/Components/Mobile/GlobalMobileSearchHeroBar'
import GlobalSearchHeroContainer from 'components/Search/GlobalSearch/GlobalSearchHeroContainer'
import GlobalSearchContainerPane, { GlobalSearchContainerPaneContent } from 'components/Search/GlobalSearch/GlobalSearchContainerPane'
import AnalyticsPageContext, { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import config from 'constants/config'
import { flightsLandingPageView } from 'analytics/eventDefinitions/index'
import { fireNonInteractionEvent } from 'api/googleTagManager'
import useFlightDeals from 'hooks/Flights/useFlightDeals'
import useFlightDealLocations from 'home/hooks/useFlightDealLocations'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import { getListName } from 'analytics/enhanced-ecommerce'
import FlightDealsCarousel from 'home/components/FlightDeals/FlightDealsCarousel/FlightDealsCarousel'
import { FlightCampaigns } from 'home/components/FlightCampaigns/FlightCampaigns'
import { useFlightCampaigns } from 'hooks/Flights/useFlightCampaigns'
import { useShowFlightCampaignsOrDeals } from 'hooks/Flights/useCampaignsOrDeals'
import useRecentFlightSearches from 'hooks/Flights/useRecentFlightSearches'
import { useIsMobileDevice } from 'hooks/useIsMobileDevice'
import useDefaultAirport from 'hooks/useDefaultAirport'
import { mapAirportToAirportLocation } from 'lib/flights/flightUtils'
import { isAgentHubEnabled } from 'agentHub/selectors/agentHubSelectors'
import { MALDIVES_CHARTER_CAMPAIGN_TAG, MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG } from 'constants/charterFlight'
import PopularFlightsSection from 'components/Flights/PopularFlights/PopularFlightsSection'
import { useExcludedFlightDestinations } from 'hooks/Flights/useExcludedFlightDestinations'

const AirlineLogos = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: ${rem(48)};
  justify-items: center;
  justify-content: center;
  align-items: center;

  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(4, ${rem(120)});
  }
`

const ContentContainer = styled(LayoutContainer)`
  padding-top: ${rem(48)};
  padding-bottom: ${rem(48)};

  > * + * {
    margin-top: ${rem(20)};
  }
`

const OfferListContainer = styled(LayoutContainer)`
  padding-top: ${rem(72)};
  padding-bottom: ${rem(40)};

  > * + * {
    margin-top: ${rem(48)};
  }
`

const FlightDealBlock = styled.div`
  padding: ${rem(48)} 0;
`

const flightDestinations = ['Melbourne', 'Los Angeles', 'London', 'Paris', 'Phuket']

type MatchProps = { dealCategory?: string }
interface Props extends RouteComponentProps<MatchProps> {
  isFlightsCredit: boolean
}

function FlightsSearchLandingPage(props: Props) {
  const intl = useIntl()
  const { currentRegionCode } = useContext(GeoContext)
  const content = FlightLandingPageRegionContent[currentRegionCode] || FlightLandingPageRegionContent.AU
  const { isFlightsCredit } = props
  const { dealCategory } = props.match.params
  const [flightDeals, fetching] = useFlightDeals(dealCategory)
  const [campaigns, fetchingCampaigns] = useFlightCampaigns()
  const [airports, defaultAirport] = useFlightDealLocations(dealCategory)
  const [selectedAirport, setAirport] = useState<App.Airport | undefined>()
  const campaignsOrDeals = useShowFlightCampaignsOrDeals()
  const recentSearchesData = useRecentFlightSearches()
  const isMobileDevice = useIsMobileDevice()

  const mobileHeroTitle = intl.formatMessage({
    id: 'g2L8FyPRAH',
    defaultMessage: 'Your journey starts here.',
    description: 'Mobile hero title on flights search landing page on the promotional landing page',
  })
  const desktopHeroTitle = intl.formatMessage({
    id: 'BJPULOQmTm',
    defaultMessage: 'Your journey starts here. Search flights',
    description: 'Desktop hero title on flights search landing page on the promotional landing page',
  })
  const searchPlaceholder = intl.formatMessage({
    id: 'Scggk9Qvu3',
    defaultMessage: 'Search flights to',
    description: 'Placeholder text for flights search bar on flights search landing page',
  })

  const prefillAirport = useDefaultAirport()
  const prefillDepartureAirport = !isMobileDevice && prefillAirport
    ? mapAirportToAirportLocation(prefillAirport)
    : undefined

  const mainContentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    fireNonInteractionEvent(flightsLandingPageView())
  }, [])

  const [locations] = useFlightDealLocations()

  const tracking = useOfferListTracking(
    getListName('OfferList', 'FLIGHTS', 'Hotel and Flight Packages Offer List'),
    { key: currentRegionCode },
  )

  const dealAirport = selectedAirport ?? defaultAirport
  const excludedDestinations = useExcludedFlightDestinations({
    campaignsOrDeals,
    flightDeals,
    dealAirport,
  })

  const showFlightDeals = fetching || (!fetching && flightDeals.length > 0)
  const showAirlineLogos = !fetching && !isFlightsCredit && !showFlightDeals
  return (
    <AnalyticsPageContext.Provider value={AnalyticsPage.verticalPage}>
      <GlobalSearchHeroContainer heroImageId={flightsImageId} title={isMobileDevice ? mobileHeroTitle : desktopHeroTitle}>
        <GlobalSearchContainerPane displayMobile={isMobileDevice || !config.USE_INSPIRATIONAL_SEARCH}>
          <GlobalSearchContainerPaneContent>
            <FlightsSearch
              fromFlightsLanding
              recentSearchesData={recentSearchesData}
              departureAirport={prefillDepartureAirport}
              autoFocusArrival={!isMobileDevice}
            />
          </GlobalSearchContainerPaneContent>
        </GlobalSearchContainerPane>
        {config.USE_INSPIRATIONAL_SEARCH && !isMobileDevice && <GlobalMobileSearchHeroBar
          placeholder={searchPlaceholder}
          placeholderTextList={flightDestinations}
        />}
      </GlobalSearchHeroContainer>

      <Pane ref={mainContentRef}>
        {showAirlineLogos && <ContentContainer size="md">
          <Heading align="center" as="h2" variant="heading3">{intl.formatMessage({
            id: 'SfywfKEZ33',
            defaultMessage: 'Fly with the world’s best airlines',
            description: 'Heading for airline logos section on flights search landing page',
          })}
          </Heading>
          <BodyText variant="large" align="center" as="p">
            {intl.formatMessage({
              id: 'qwEqE+9+Vd',
              defaultMessage: 'Whether you are a loyal frequent flyer member, prefer a select airline or simply want the best value fare, we have you covered.',
              description: 'Description text for airline logos section on flights search landing page',
            })}
          </BodyText>
          <AirlineLogos>
            {content.airlineLogos?.map((airline) => (
              <Image
                id={airline.id}
                alt={airline.alt}
                width={airline.width}
                key={airline.id}
              />
            ))}
          </AirlineLogos>
        </ContentContainer>}

        <FlightsCreditView />

        {(!isAgentHubEnabled && campaignsOrDeals !== 'none') && (
          <>
            {campaignsOrDeals === 'campaigns' && campaigns.length > 0 && (
              <FlightDealBlock>
                <FlightCampaigns campaigns={campaigns} loading={fetchingCampaigns} />
              </FlightDealBlock>
            )}

            {campaignsOrDeals === 'deals' && flightDeals.length > 0 && (
              <FlightDealBlock>
                <FlightDealsCarousel
                  title={locations.length > 1
                    ? intl.formatMessage({ id: 'jS9cAjV/hY', defaultMessage: 'Best flight deals from', description: 'Title for flight deals carousel when multiple departure locations are available' })
                    : intl.formatMessage({ id: 'STHG8k6VxX', defaultMessage: 'Best flight deals', description: 'Title for flight deals carousel in the flight search or booking flow' })}
                  airports={airports}
                  flightDeals={flightDeals}
                  onAirportChange={setAirport}
                  defaultAirport={defaultAirport}
                  selectedAirport={selectedAirport}
                  loading={fetching}
                />
              </FlightDealBlock>
            )}
          </>
        )}
        {!fetching && <PopularFlightsSection excludedDestinations={excludedDestinations} />}
      </Pane>
      <Pane background="neutral-six">
        <OfferListContainer>
          <Heading align="center" variant="heading3">{intl.formatMessage({
            id: 'Y6V0k0wRv6',
            defaultMessage: 'Browse our hotel and flight packages',
            description: 'Heading for hotel and flight packages offer list on flights search landing page',
          })}
          </Heading>
          <OfferListEventsProvider tracking={tracking}>
            <OfferList filters={{
              offerTypes: HOTEL_PAGE_OFFER_TYPES(currentRegionCode),
              frontPageVisibleOnly: true,
              ...(isAgentHubEnabled ? { campaigns: [MALDIVES_CHARTER_CAMPAIGN_TAG, MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG] } : {}),
            }}
            />
          </OfferListEventsProvider>
        </OfferListContainer>
      </Pane>
    </AnalyticsPageContext.Provider>
  )
}

function mapStateToProps(state: App.State) {
  return {
    isFlightsCredit: isFlightsCredit(state),
  }
}

export default connect(mapStateToProps)(FlightsSearchLandingPage)
